import React, { useState, useEffect, useLayoutEffect, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, Navigate, useParams } from 'react-router-dom';
import { Spinner } from 'react-bootstrap';
import { PublicClientApplication } from '@azure/msal-browser';
import { MsalProvider } from '@azure/msal-react';
import { msalConfig } from './config/msalConfig';
import 'bootstrap/dist/css/bootstrap.min.css';
import Sidebar from './components/Sidebar';
import { Login, ResetPassword, VerifyEmail, ProtectedRoute, GoogleCallback } from './auth';
import Profile from './components/Profile';
import InitialRedirect from './components/InitialRedirect';
import PrivacyPolicy from './components/PrivacyPolicy';
import TermsOfService from './components/TermsOfService';
import './App.css';
import './components/Global.css';
import './components/ui/Modal.css';
import { getAutomateApiUrl } from './automate/apiBase';
import { installAutomateHttpInterceptors } from './automate/automateHttp';

installAutomateHttpInterceptors();

export { getFeaturePath } from './utils/routeUtils';

// Lazy load heavy components
const RequirementIntake = React.lazy(() => import('./components/RequirementIntake'));
const Home = React.lazy(() => import('./components/Home'));
const RequestHistoryPage = React.lazy(() => import('./components/RequestHistoryPage'));
const TestAiHome = React.lazy(() => import('./components/testai/TestAiHome'));
const TestAiNew = React.lazy(() => import('./components/testai/TestAiNew'));
const TestAiRuns = React.lazy(() => import('./components/testai/TestAiRuns'));
const TestAiSuite = React.lazy(() => import('./components/testai/TestAiSuite'));
const TestAiRun = React.lazy(() => import('./components/testai/TestAiRun'));
const AutomationScriptGeneration = React.lazy(() => import('./components/automationScripts/Automationscriptgeneration'));
const LensLibrary = React.lazy(() => import('./components/lenslibrary/LensLibrary'));
const BugReport = React.lazy(() => import('./components/BugReportHome'));
const SharedBugReport = React.lazy(() => import('./components/SharedBugReport'));
const BrdToFrdPage = React.lazy(() => import('./components/brd-to-frd/BrdToFrdPage'));

// Loading component
const LoadingSpinner = () => (
  <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '200px' }}>
    <Spinner animation="border" role="status">
      <span className="visually-hidden">Loading...</span>
    </Spinner>
  </div>
);

const ProjectToLens = () => {
  const { id } = useParams();
  return <Navigate to={`/lens-library/projectId/${id}`} replace />;
};

// Automate holds one project's request, sources and polling; switching project mounts a
// fresh page so nothing from the previous project stays on screen or keeps polling.
const ProjectScopedRequirementIntake = () => {
  const { projectId } = useParams();
  const { search } = useLocation();
  const scope = projectId || new URLSearchParams(search).get('projectId') || 'none';
  return <RequirementIntake key={scope} />;
};

const AppShell = ({ isAuthenticated, setIsAuthenticated }) => {
  const location = useLocation();
  const [userProfile, setUserProfile] = useState(null);
  const isAuthRoute = ['/login', '/google-callback', '/reset-password', '/verify-email'].some((p) => location.pathname.startsWith(p));
  // Public share pages (e.g. /shared/bug-report/:token) are standalone: no app sidebar, even when signed in.
  const isSharedRoute = location.pathname.startsWith('/shared/');
  const showSidebar = isAuthenticated && !isAuthRoute && !isSharedRoute;

  // Scroll to top on route change - use useLayoutEffect for synchronous execution
  useLayoutEffect(() => {
    // Immediate scroll to top (no smooth behavior for instant effect)
    window.scrollTo(0, 0);
    if (document.documentElement) {
      document.documentElement.scrollTop = 0;
    }
    if (document.body) {
      document.body.scrollTop = 0;
    }
  }, [location.pathname]);
  
  // Also use useEffect as a backup to catch any late-rendering content
  useEffect(() => {
    // Scroll after a small delay to catch any late-rendering content
    const timeoutId = setTimeout(() => {
      window.scrollTo(0, 0);
      if (document.documentElement) {
        document.documentElement.scrollTop = 0;
      }
      if (document.body) {
        document.body.scrollTop = 0;
      }
      
      // Also scroll any scrollable containers to top
      const scrollableContainers = document.querySelectorAll('[data-scroll-container], .main-content, .scrollable');
      scrollableContainers.forEach(container => {
        if (container && container.scrollTop !== undefined) {
          container.scrollTop = 0;
        }
      });
    }, 0);
    
    return () => clearTimeout(timeoutId);
  }, [location.pathname]);

  // Fetch user profile when authenticated
  useEffect(() => {
    if (isAuthenticated && !isAuthRoute) {
      const fetchUserProfile = async () => {
        try {
          const token = localStorage.getItem('token') || sessionStorage.getItem('token');
          if (token) {
            const response = await fetch(`${getAutomateApiUrl()}/auth/profile`, {
              headers: { Authorization: `Bearer ${token}` },
            });
            if (response.ok) {
              const profileData = await response.json();
              setUserProfile(profileData);
            }
          }
        } catch (error) {
          console.error('App.js: Error fetching user profile:', error);
        }
      };

      // Fetch profile immediately after authentication
      fetchUserProfile();
    }
  }, [isAuthenticated, isAuthRoute]);

  return (
    <div className={`app-container ${showSidebar ? 'has-sidebar' : ''} ${isAuthRoute ? 'auth' : ''}`} style={isAuthRoute ? { background: '#ffffff' } : {}}>
      {showSidebar && <Sidebar setIsAuthenticated={setIsAuthenticated} userProfile={userProfile} />}
      <div className="main-content">
        <Suspense fallback={<LoadingSpinner />}>
          <Routes>
            <Route path="/" element={<InitialRedirect />} />
            <Route
              path="/home"
              element={
                <ProtectedRoute setIsAuthenticated={setIsAuthenticated}>
                  <Home userProfile={userProfile} />
                </ProtectedRoute>
              }
            />
            <Route
              path="/project/:id"
              element={
                <ProtectedRoute setIsAuthenticated={setIsAuthenticated}>
                  <ProjectToLens />
                </ProtectedRoute>
              }
            />
            <Route
              path="/requirement-intake/projectId/:projectId"
              element={
                <ProtectedRoute setIsAuthenticated={setIsAuthenticated}>
                  <ProjectScopedRequirementIntake />
                </ProtectedRoute>
              }
            />
            <Route
              path="/requirement-intake"
              element={
                <ProtectedRoute setIsAuthenticated={setIsAuthenticated}>
                  <ProjectScopedRequirementIntake />
                </ProtectedRoute>
              }
            />
            <Route
              path="/test-automation"
              element={
                <ProtectedRoute setIsAuthenticated={setIsAuthenticated}>
                  <TestAiHome />
                </ProtectedRoute>
              }
            />
            <Route
              path="/test-automation/projectId/:projectId"
              element={
                <ProtectedRoute setIsAuthenticated={setIsAuthenticated}>
                  <TestAiHome />
                </ProtectedRoute>
              }
            />
            <Route
              path="/test-automation/projectId/:projectId/new"
              element={
                <ProtectedRoute setIsAuthenticated={setIsAuthenticated}>
                  <TestAiNew />
                </ProtectedRoute>
              }
            />
            <Route
              path="/test-automation/projectId/:projectId/runs"
              element={
                <ProtectedRoute setIsAuthenticated={setIsAuthenticated}>
                  <TestAiRuns />
                </ProtectedRoute>
              }
            />
            <Route
              path="/test-automation/projectId/:projectId/suites/:suiteId"
              element={
                <ProtectedRoute setIsAuthenticated={setIsAuthenticated}>
                  <TestAiSuite />
                </ProtectedRoute>
              }
            />
            <Route
              path="/test-automation/projectId/:projectId/runs/:runId"
              element={
                <ProtectedRoute setIsAuthenticated={setIsAuthenticated}>
                  <TestAiRun />
                </ProtectedRoute>
              }
            />
            <Route
              path="/automation-scripts"
              element={
                <ProtectedRoute setIsAuthenticated={setIsAuthenticated}>
                  <AutomationScriptGeneration />
                </ProtectedRoute>
              }
            />
            <Route
              path="/automation-scripts/projectId/:projectId"
              element={
                <ProtectedRoute setIsAuthenticated={setIsAuthenticated}>
                  <AutomationScriptGeneration />
                </ProtectedRoute>
              }
            />
            <Route
              path="/lens-library"
              element={
                <ProtectedRoute setIsAuthenticated={setIsAuthenticated}>
                  <LensLibrary />
                </ProtectedRoute>
              }
            />
            <Route
              path="/lens-library/projectId/:projectId"
              element={
                <ProtectedRoute setIsAuthenticated={setIsAuthenticated}>
                  <LensLibrary />
                </ProtectedRoute>
              }
            />
            
       
            <Route
              path="/bug-report"
              element={
                <ProtectedRoute setIsAuthenticated={setIsAuthenticated}>
                  <BugReport />
                </ProtectedRoute>
              }
            />
            <Route
              path="/bug-report/projectId/:projectId"
              element={
                <ProtectedRoute setIsAuthenticated={setIsAuthenticated}>
                  <BugReport />
                </ProtectedRoute>
              }
            />
            <Route
              path="/profile"
              element={
                <ProtectedRoute setIsAuthenticated={setIsAuthenticated}>
                  <Profile setUserProfile={setUserProfile} userProfile={userProfile} />
                </ProtectedRoute>
              }
            />
            <Route
              path="/request-history"
              element={
                <ProtectedRoute setIsAuthenticated={setIsAuthenticated}>
                  <RequestHistoryPage userProfile={userProfile} />
                </ProtectedRoute>
              }
            />
            <Route
              path="/brd-to-frd"
              element={
                <ProtectedRoute setIsAuthenticated={setIsAuthenticated}>
                  <BrdToFrdPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/brd-to-frd/projectId/:projectId"
              element={
                <ProtectedRoute setIsAuthenticated={setIsAuthenticated}>
                  <BrdToFrdPage />
                </ProtectedRoute>
              }
            />

            <Route path="/login" element={<Login setIsAuthenticated={setIsAuthenticated} />} />
            <Route path="/reset-password" element={<ResetPassword setIsAuthenticated={setIsAuthenticated} />} />
            <Route path="/verify-email/:token" element={<VerifyEmail />} />
            <Route path="/google-callback" element={<GoogleCallback />} />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/terms-of-service" element={<TermsOfService />} />
            <Route path="/shared/bug-report/:token" element={<SharedBugReport />} />
          </Routes>
        </Suspense>
      </div>
    </div>
  );
};

const App = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // Create MSAL instance
  const msalInstance = new PublicClientApplication(msalConfig);

  useEffect(() => {
    const token = localStorage.getItem('token') || sessionStorage.getItem('token');
    setIsAuthenticated(!!token);
  }, []);

  return (
    <MsalProvider instance={msalInstance}>
      <Router>
        <AppShell isAuthenticated={isAuthenticated} setIsAuthenticated={setIsAuthenticated} />
      </Router>
    </MsalProvider>
  );
};

export default App;
